import { Injectable } from "@angular/core";
import { Observable, map, take } from "rxjs";
import { AccountResolverService } from "@gtmhub/state/account-resolver-service";
import { CurrentUserService } from "@gtmhub/users";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import dayjs from "@webapp/shared/libs/dayjs";

// initial launch date - the feat flag is raised on this date and users have a toggle setting available in their profiles to switch between the new and the old home
const TOGGLEABLE_RELEASE_DATE = dayjs("2023/12/12");

// the toggle is removed, users start seeing the new home regardless of what they had previously set in their user settings
const NON_TOGGLEABLE_RELEASE_DATE = dayjs("2024/02/01");

@Injectable()
export class NewHomeExperienceService {
  public constructor(
    private accountResolverService: AccountResolverService,
    private currentUserService: CurrentUserService,
    private featureTogglesFacade: FeatureTogglesFacade
  ) {}

  public setNewHomeExperienceSetting(options: { isEnabled: boolean }): void {
    this.currentUserService.setUserSetting({ newHomeExperience: options.isEnabled });
  }

  public showBetaLabels(): boolean {
    // the features are considered in BETA until {TOGGLEABLE_RELEASE_DATE}
    return dayjs().isBefore(TOGGLEABLE_RELEASE_DATE);
  }

  public shouldDisallowTogglingNewHome(): boolean {
    // accounts created after {TOGGLEABLE_RELEASE_DATE} are disallowed to switch to the old home, navigation and team profile
    // after {NON_TOGGLEABLE_RELEASE_DATE} all users are disallowed to switch to the old home, navigation and team profile
    return this.isAccountCreatedAfterToggleableRelease() || this.isTodayAfterNonToggleableRelease();
  }

  public isNewHomeExperienceSettingEnabled(): boolean {
    return (
      // after the {NON_TOGGLEABLE_RELEASE_DATE} all accounts start seeing the new home regardless of whether they had the feature enabled or disabled from the user settings
      this.isTodayAfterNonToggleableRelease() ||
      // accounts created after {TOGGLEABLE_RELEASE_DATE} are seeing the new stuff only without the option to toggle to the old home, navigation and team profile
      this.isAccountCreatedAfterToggleableRelease() ||
      // if the former two are false, check the toggle from the user settings (undefined from the backend is interpreted as true - i.e. enabled by default)
      this.currentUserService.getUserSetting<boolean>("newHomeExperience") !== false
    );
  }

  public isNewHomeDashboardEnabled$(): Observable<boolean> {
    return this.featureTogglesFacade.isFeatureAvailable$(FeatureFlag.HomeNext).pipe(
      take(1),
      map((isFlagEnabled) => isFlagEnabled && this.isNewHomeExperienceSettingEnabled())
    );
  }

  public isNewNavigationEnabled$(): Observable<boolean> {
    return this.featureTogglesFacade.isFeatureAvailable$(FeatureFlag.NextTopNavBar).pipe(
      take(1),
      map((isFlagEnabled) => {
        // after {TOGGLEABLE_RELEASE_DATE} the new navigation (both top-bar and left nav) is no longer controlled by the new home toggle in the user settings
        const isSettingEnabled = this.isNewHomeExperienceSettingEnabled() || this.isTodayAfterToggleableRelease();
        return isFlagEnabled && isSettingEnabled;
      })
    );
  }

  public areInlinedTeamProfileTabsEnabled$(): Observable<boolean> {
    return this.featureTogglesFacade.isFeatureAvailable$(FeatureFlag.TeamProfileOkrsTab).pipe(
      take(1),
      map((areInlinedTabsFlagEnabled) => {
        // after {TOGGLEABLE_RELEASE_DATE} the okrs tab in the team profile is no longer controlled by the new home toggle in the user settings
        const isSettingEnabled = this.isNewHomeExperienceSettingEnabled() || this.isTodayAfterToggleableRelease();
        return areInlinedTabsFlagEnabled && isSettingEnabled;
      })
    );
  }

  private isAccountCreatedAfterToggleableRelease(): boolean {
    const dateOfAccountCreation = this.accountResolverService.getAccountData()?.dateCreated;
    return dateOfAccountCreation && dayjs(dateOfAccountCreation).isSameOrAfter(TOGGLEABLE_RELEASE_DATE);
  }

  private isTodayAfterToggleableRelease(): boolean {
    return dayjs().isSameOrAfter(TOGGLEABLE_RELEASE_DATE);
  }

  private isTodayAfterNonToggleableRelease(): boolean {
    return dayjs().isSameOrAfter(NON_TOGGLEABLE_RELEASE_DATE);
  }
}
